import {ComponentMappingStatusSummary} from '../../../../../@types/component-mapping-state';
import React from 'react';
import {IntegratedRiskPicture} from '../../../../../@types/integrated-risk-picture';
import EventSequence from '../../../../../@types/event-sequence';
import {FaultTree} from '../../../../../@types/faultTree';
import {
    createIRPWorkbookStore, IRPWorkbookStoreContext, IRPWorkbookStoreInstance,
} from '../../../../../stores/irp-workbook.store';
import eventSequenceService from '../../../../../services/event-sequence.service';
import newOccurrenceService from '../../../../../services/new-occurrence.service';
import irpService from '../../../../../services/irp.service';
import componentService from '../../../../../services/component.service';
import {ComponentWorkbookOverview} from '../../../../../services/component-workbook.service';
import {
    createComponentWorkbookDataSource,
    createIrpWorkbookDataSource,
    WorkbookDataSource,
} from './workbook-data-source';

/**
 * Identifies the (component version, report source) pair a workbook shows in component mode:
 * one (component, report source) pair.
 */
export interface ComponentMappingScope {
    componentId: number;
    sourceName: string;
}

/**
 * Props for the IRPWorkbookProvider component. Exactly one of integratedRiskPicture (IRP mode)
 * or componentMapping (component mapping mode) should be supplied.
 */
interface IRPWorkbookProviderProps {
    /**
     * The IRP data to display in the workbook
     */
    integratedRiskPicture?: IntegratedRiskPicture;

    /**
     * The component mapping scope to display instead of an IRP. In this mode the workbook
     * renders the component's fault tree and its mapping records through the same tabs.
     */
    componentMapping?: ComponentMappingScope;

    /**
     * Children components to render within the provider
     */
    children: React.ReactNode;
}

/**
 * Both dendrograms and the barrier tabs read the fault tree via
 * eventSequenceData.events[0].faultTree, so component mode stores the component's fault tree
 * inside this minimal synthetic envelope instead of changing every consumer. Only that one
 * unwrap path is ever read in component mode, hence the narrowing cast.
 */
const buildSyntheticEventSequence = (faultTree: FaultTree): EventSequence =>
    ({events: [{faultTree}]} as unknown as EventSequence);

/**
 * Provider component that wraps the IRP Workbook with necessary context.
 *
 * Creates an isolated Zustand store instance per IRP workbook tab so that
 * multiple open IRPs have fully independent filter/cache/navigation state.
 *
 * On mount/IRP change:
 *   1. Calls initializeWorkbook to clear all stale BFF caches (Task 15.1)
 *   2. Writes irpDetails and eventSequenceData to the store (Task 15.2)
 *   3. Loads availableDescriptors for PrimaryFilter once on open (Task 15.3)
 *
 * In component mapping mode there is no IRP and no event sequence: the provider fetches the
 * component (with its fault tree), stores it in a synthetic event-sequence envelope so the
 * dendrograms render, and routes all tab fetches through the component data source.
 *
 * @component
 * @example
 * ```tsx
 * <IRPWorkbookProvider integratedRiskPicture={irpData}>
 *   <IRPWorkbook />
 * </IRPWorkbookProvider>
 * ```
 */
export const IRPWorkbookProvider: React.FC<IRPWorkbookProviderProps> = ({
    integratedRiskPicture,
    componentMapping,
    children
}) => {
    // Create the store instance exactly once per provider mount.
    // Using a ref (not useState/useMemo) avoids re-creation on re-renders and
    // guarantees the same instance is passed to context on every render.
    const storeRef = React.useRef<IRPWorkbookStoreInstance | null>(null);
    if (!storeRef.current) {
        storeRef.current = createIRPWorkbookStore();
    }

    // State to manage IRP details
    const [irpDetails, setIrpDetails] = React.useState<IntegratedRiskPicture | undefined>(integratedRiskPicture);

    // Component mode: the header fields, refreshed whenever the overview is fetched.
    const [mappingOverview, setMappingOverview] = React.useState<ComponentWorkbookOverview | null>(null);

    // Update irpDetails when prop changes
    React.useEffect(() => {
        setIrpDetails(integratedRiskPicture);
    }, [integratedRiskPicture]);

    // Task 15.1 + 15.2: On IRP change, clear all caches and write shared data to store
    React.useEffect(() => {
        if (componentMapping || !integratedRiskPicture?.id) return;

        const store = storeRef.current!.getState();

        // 15.1 + pre-populate PrimaryFilter: atomically initialize with irpId and any
        // date criteria from the IRP's reportSearchCriteria. Using initializeWorkbook
        // instead of separate setIrpId + setPrimaryFilter calls avoids triggering two
        // cache-clear cycles (which would cause two sets of BFF fetches on load).
        const criteria = integratedRiskPicture.reportSearchCriteria;
        const initialFilter = (criteria?.startDate || criteria?.endDate) ? {
            dateFrom: criteria.startDate ? new Date(criteria.startDate) : null,
            dateTo: criteria.endDate ? new Date(criteria.endDate) : null,
            selectedDescriptors: new Set<number>(),
            selectedSources: new Set<string>(),
        } : undefined;
        store.initializeWorkbook(integratedRiskPicture.id, initialFilter);

        // 15.2: Write IRP details to store
        store.setIrpDetails(integratedRiskPicture);

        // 15.2: Fetch full IRP by ID to get simpleEventSequences (lazy-loaded on entity,
        // not guaranteed to be present in list-view prop).
        irpService.getIntegratedRiskPictureById(integratedRiskPicture.id)
            .then((fullIrp) => {
                if (!fullIrp) return;
                // Update store with the more complete full-detail IRP
                storeRef.current!.getState().setIrpDetails(fullIrp);

                // Get ESD ID and version snapshot ID from simpleEventSequences
                const firstSeq = fullIrp.simpleEventSequences?.[0];
                const esdId = firstSeq?.eventSequenceId ?? fullIrp.eventSequenceId;
                if (!esdId) return;

                // Use the version snapshot recorded at mapping time when available,
                // so the dendrograph reflects the ESD as it was when the IRP was created.
                const esdFetch = firstSeq?.versionId
                    ? eventSequenceService.getEventSequenceAtVersion(esdId, firstSeq.versionId)
                    : eventSequenceService.getEventSequenceById(esdId);

                esdFetch
                    .then((esd: any) => {
                        storeRef.current!.getState().setEventSequenceData(esd);
                    })
                    .catch(() => {
                        // null = fetch completed but no data (distinguishes from undefined = still loading)
                        storeRef.current!.getState().setEventSequenceData(null);
                    });
            })
            .catch(() => {
                // Non-fatal: ESD chip will show "not loaded" and dendrogram will show empty state
            });
    }, [componentMapping, integratedRiskPicture?.id]); // re-run only when the IRP changes

    // Component mode boot: clear caches under a neutral scope key (each provider mount has its own
    // store instance, so the key only needs to change with the scope), then load the component's
    // fault tree into the synthetic event-sequence envelope for the dendrograms.
    React.useEffect(() => {
        if (!componentMapping) return;

        setMappingOverview(null);
        storeRef.current!.getState().initializeWorkbook(componentMapping.componentId);

        componentService.getComponent(componentMapping.componentId)
            .then((component) => {
                storeRef.current!.getState().setEventSequenceData(
                    component.faultTree ? buildSyntheticEventSequence(component.faultTree) : null);
            })
            .catch(() => {
                // null = fetch completed but no data (dendrograms show their empty state)
                storeRef.current!.getState().setEventSequenceData(null);
            });
    }, [componentMapping?.componentId, componentMapping?.sourceName]);

    // Task 15.3: Load available descriptor terms once on workbook open
    React.useEffect(() => {
        newOccurrenceService.getAvailableDescriptorTerms()
            .then((terms) => {
                storeRef.current!.getState().setAvailableDescriptors(terms);
            })
            .catch(() => {
                // Non-fatal: descriptor filter will simply show no options
            });
    }, []);

    // The data source every tab fetches through; stable per scope so effects keyed on it
    // do not re-fire on unrelated renders.
    const dataSource = React.useMemo((): WorkbookDataSource | null => {
        if (componentMapping) {
            return createComponentWorkbookDataSource(
                componentMapping.componentId, componentMapping.sourceName, setMappingOverview);
        }
        return integratedRiskPicture?.id ? createIrpWorkbookDataSource(integratedRiskPicture.id) : null;
    }, [componentMapping?.componentId, componentMapping?.sourceName, integratedRiskPicture?.id]);

    // Store the IRP ID in a context or pass it down as needed
    // This will be used by child components to fetch data
    const irpContext = React.useMemo(() => ({
        irpId: irpDetails?.id,
        irpDetails,
        irpStatus: irpDetails?.status,
        // A component's mapping is a read-only view of what a run recorded; IRPs lock when closed out.
        isReadOnly: !!componentMapping ||
                   irpDetails?.status === 'COMPLETED' ||
                   irpDetails?.status === 'ARCHIVED',
        setIrpDetails,
        dataSource,
        componentMapping: componentMapping ? {
            ...componentMapping,
            lastRunAt: mappingOverview?.lastRunAt,
            mappingStatus: mappingOverview?.mappingStatus,
        } : undefined,
    }), [irpDetails, dataSource, componentMapping, mappingOverview]);

    return (
        <IRPWorkbookStoreContext.Provider value={storeRef.current}>
            <IRPContext.Provider value={irpContext}>
                {children}
            </IRPContext.Provider>
        </IRPWorkbookStoreContext.Provider>
    );
};

/**
 * Component mapping details exposed to workbook children in component mode.
 * The lifecycle fields arrive with the first overview fetch and may be briefly undefined.
 */
export interface ComponentMappingContextValue extends ComponentMappingScope {
    lastRunAt?: string;
    mappingStatus?: ComponentMappingStatusSummary;
}

/**
 * Context for sharing IRP-specific data throughout the workbook
 */
interface IRPContextValue {
    irpId?: number;
    irpDetails?: IntegratedRiskPicture;
    irpStatus?: string;
    isReadOnly: boolean;
    setIrpDetails: (irp: IntegratedRiskPicture) => void;
    /** How the tabs fetch workbook data; null until the scope (IRP or component) is known. */
    dataSource: WorkbookDataSource | null;
    /** Present only in component mapping mode. */
    componentMapping?: ComponentMappingContextValue;
}

const IRPContext = React.createContext<IRPContextValue>({
    irpId: undefined,
    irpDetails: undefined,
    irpStatus: undefined,
    isReadOnly: true,
    setIrpDetails: () => {},
    dataSource: null,
    componentMapping: undefined,
});

/**
 * Custom hook to access IRP context within the workbook
 * @returns The IRP context value
 * @throws Error if used outside of IRPWorkbookProvider
 */
export const useIRPContext = (): IRPContextValue => {
    const context = React.useContext(IRPContext);

    if (!context) {
        throw new Error('useIRPContext must be used within an IRPWorkbookProvider');
    }

    return context;
};

// Export the provider as default for the index file
export default IRPWorkbookProvider;
